import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calculator, ChevronLeft, Info, Loader2 } from "lucide-react";
import { API, JURISDICTIONS, DEADLINE_TYPES, SERVICE_METHODS } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";

const DeadlinesPage = () => {
  const [form, setForm] = useState({
    trigger_date: new Date().toISOString().split('T')[0],
    deadline_type: "answer",
    jurisdiction: "federal",
    service_method: "personal"
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const calculate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/deadlines/calculate`, form);
      setResult(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="deadlines-page">
      <Disclaimer />
      <div className="container-shell py-8 max-w-4xl">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Deadline Calculator</h1>
          <p className="text-muted-foreground">Calculate federal and state court deadlines</p>
        </div>
        
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader><CardTitle>Calculate Deadline</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label>Trigger Date</Label>
                <Input 
                  type="date" 
                  value={form.trigger_date}
                  onChange={e => setForm(p => ({ ...p, trigger_date: e.target.value }))}
                />
              </div>
              
              <div className="space-y-1.5">
                <Label>Jurisdiction</Label>
                <Select value={form.jurisdiction} onValueChange={v => setForm(p => ({ ...p, jurisdiction: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {JURISDICTIONS.map(j => <SelectItem key={j.value} value={j.value}>{j.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1.5">
                <Label>Deadline Type</Label>
                <Select value={form.deadline_type} onValueChange={v => setForm(p => ({ ...p, deadline_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DEADLINE_TYPES.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1.5">
                <Label>Service Method</Label>
                <Select value={form.service_method} onValueChange={v => setForm(p => ({ ...p, service_method: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SERVICE_METHODS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              
              <Button className="w-full" onClick={calculate} disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Calculator className="h-4 w-4 mr-2" />}
                Calculate Deadline
              </Button>
            </CardContent>
          </Card>
          
          {result && (
            <Card className="animate-fade-in">
              <CardHeader><CardTitle>Result</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-primary/5 rounded-xl">
                  <p className="text-sm text-muted-foreground mb-1">Deadline Date</p>
                  <p className="text-3xl font-bold text-primary">{result.deadline_date}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Days Until</p>
                    <p className="text-xl font-semibold">{result.days_until}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Rule Reference</p>
                    <p className="text-sm font-medium">{result.rule_reference}</p>
                  </div>
                </div>
                
                {result.notes?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-2">Notes</p>
                    <ul className="space-y-1">
                      {result.notes.map((n, i) => (
                        <li key={i} className="text-sm text-muted-foreground flex gap-2">
                          <Info className="h-4 w-4 mt-0.5 shrink-0" /> {n}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      <Footer />
    </main>
  );
};


export default DeadlinesPage;
